import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Collaborator } from "@/types";
import { Check, ChevronsUpDown, User } from "lucide-react";
import React, { useContext } from "react";

type CollaboratorPickerProps = {
  collaborators: Collaborator[];
  handleFieldCollaborators: (id: number) => void;
  collaboratorRef: React.MutableRefObject<HTMLDivElement | null>;
  side?: "left" | "right" | "top" | "bottom" | undefined;
  align?: "center" | "end" | "start" | undefined;
  containerClassName?: string;
  portalContainer?: HTMLElement | null;
};

const CollaboratorPicker: React.FC<CollaboratorPickerProps> = ({
  collaborators,
  handleFieldCollaborators,
  collaboratorRef,
  side,
  align,
  containerClassName,
  portalContainer,
}) => {
  const { allAppUsers } = useContext(AppContext);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={collaboratorRef} className={containerClassName}>
      <Popover>
        <PopoverTrigger
          className="w-full"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          asChild
        >
          <Button
            id="edit-collaborators"
            variant="outline"
            role="combobox"
            className="justify-between w-full"
            onMouseDown={handleMouseDown}
          >
            {collaborators && collaborators.length > 0
              ? `${collaborators.length} selected`
              : "Select Collaborators"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          container={portalContainer ?? undefined}
          side={side ?? undefined}
          align={align ?? undefined}
          className="w-full p-0"
          ref={collaboratorRef}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Command>
            <CommandInput placeholder="Search collaborators..." />
            <CommandList>
              <ScrollArea
                className="h-[200px]"
                onWheel={(e) => e.stopPropagation()}
              >
                <CommandEmpty>No collaborators found.</CommandEmpty>
                <CommandGroup>
                  {allAppUsers.map((collaborator) => (
                    <CommandItem
                      key={collaborator.id}
                      onSelect={() => handleFieldCollaborators(collaborator.id)}
                      onMouseDown={handleMouseDown}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          collaborators?.some((c) => c.id === collaborator.id)
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                      <div className="flex items-center gap-2">
                        {collaborator.profile_picture ? (
                          <img
                            className="size-4 rounded-full bg-primary shrink-0"
                            src={collaborator.profile_picture}
                            alt={
                              collaborator.first_name +
                              " " +
                              collaborator.surname
                            }
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}
                        <div>
                          {collaborator.first_name} {collaborator.surname}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CollaboratorPicker;
